import React, { useState, useEffect, use } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Button,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Grid,
  Stack,
  Alert
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Circle as CircleIcon,
  Chat as ChatIcon,
  Code as CodeIcon,
  Info as InfoIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { toast } from 'sonner';
import { deploymentInstanceService } from '../../services/DeploymentInstanceService';
import { ROUTE_PATHS } from '../../constants/routePaths';
import { PlayGround } from './PlayGround';
import { APIIntegration } from './APIIntegration';
import { useModels } from '../../hooks/useModels';
import { routingService } from '../../services/RoutingService';
export interface DeployedInstance {
  id: string;
  instance_name: string;
  modelId: string;
  model_name: string;
  modelVersion: string;
  instance_status: 'Running' | 'Stopped' | 'Deploying' | 'Error';
  end_point: string;
  instance_config: {
    region: string;
    deployed_at: string;
    model_name: string;
    provider: string;
  };
  instanceType: string;
  created_at: Date;
  lastAccessed: Date;
  requestCount: number;
  avgLatency: number;
  uptime: number;
  cost: number;
  replicas: number;
  cpu: number;
  memory: number;
  autoScaling: boolean;
  service_config: {
    port: number;
    protocol: string;
    service_name: string;
    target_port: string;
  };
  guardrail_id?: string;
}

export function DeploymentDetailPage() {
  const { deploymentId = '' } = useParams<{ deploymentId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [deployment, setDeployment] = useState<DeployedInstance | null>(null);
  const [deploymentInstance, setDeploymentInstance] = useState<DeployedInstance | null>(null);
  const [routingDetail, setRoutingDetail] = useState<any>({});
  const { models } = useModels();

  useEffect(() => {
    async function fetchDeployment() {
      const newModel = await deploymentInstanceService.getDeploymentInstanceById(deploymentId);
      setDeployment(newModel);
      setIsRunning(newModel?.instance_status.toLowerCase() === 'running');
      setLoading(false);
    }
    fetchDeployment();
  }, [deploymentId]);

  useEffect(() => {
    async function fetchDeploymentInstanceConfig() {
      const config = await deploymentInstanceService.deploymentInstancesConfigsById(deploymentId);
      setDeploymentInstance(config);
    }
    fetchDeploymentInstanceConfig();
  }, [deploymentId]);

  useEffect(() => {
    async function fetchRoutingInfo() {
      try {
        const result = await routingService.getRouteByInstanceId(deploymentId);
        setRoutingDetail(result);
      } catch (error) {
        console.error('Error fetching routing info:', error);
        setRoutingDetail({});
      }
    }
    fetchRoutingInfo();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', bgcolor: 'grey.50' }}>
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!deployment) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#fafbfc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Container maxWidth="sm">
          <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '1px solid #e2e8f0' }}>
            <ErrorIcon sx={{ fontSize: 64, color: '#ef4444', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#0f172a' }}>
              Deployment Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              The deployment you're looking for doesn't exist or has been removed.
            </Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(ROUTE_PATHS.DEPLOYMENTS)}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6b3f94 100%)'
                }
              }}
            >
              Back to Deployments
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  const getStatusIcon = (status: DeployedInstance['instance_status']) => {
    switch (status) {
      case 'Running':
        return <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />;
      case 'Stopped':
        return <CircleIcon sx={{ color: '#64748b', fontSize: 20 }} />;
      case 'Deploying':
        return <PendingIcon sx={{ color: '#f59e0b', fontSize: 20 }} />;
      case 'Error':
        return <ErrorIcon sx={{ color: '#ef4444', fontSize: 20 }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: DeployedInstance['instance_status']) => {
    switch (status) {
      case 'Running':
        return { bg: '#dcfce7', text: '#166534', border: '#86efac' };
      case 'Stopped':
        return { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' };
      case 'Deploying':
        return { bg: '#fef3c7', text: '#92400e', border: '#fde047' };
      case 'Error':
        return { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' };
      default:
        return { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' };
    }
  };

  const statusColors = getStatusColor(deployment.instance_status);

  const handleInstanceAction = async (action: 'start' | 'stop' | 'restart') => {
    try {
      setLoading(true);
      if (action === 'start') await deploymentInstanceService.startInstance(deploymentId);
      if (action === 'stop') await deploymentInstanceService.stopInstance(deploymentId);
      if (action === 'restart') await deploymentInstanceService.restartInstance(deploymentId);
      setLoading(false);
      toast.success(`Instance ${action}ed successfully`, { duration: 4000 });
  navigate(ROUTE_PATHS.DEPLOYMENTS);
    } catch {
      toast.error(`Failed to ${action} instance`, { duration: 4000 });
    }
  };

  
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafbfc' }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid #e2e8f0',
          pt: 3,
          pb: 3
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                onClick={() => navigate(ROUTE_PATHS.DEPLOYMENTS)}
                sx={{
                  color: '#64748b',
                  p: 0.5,
                  height: 'fit-content',
                  alignSelf: 'center',
                  '&:hover': {
                    bgcolor: '#f8fafc'
                  }
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 1 }}>
                  {deployment.instance_name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  {(() => {
                    const statusIcon = getStatusIcon(deployment.instance_status);
                    if (statusIcon) {
                      return (
                        <Chip
                          icon={statusIcon}
                          label={deployment?.instance_status?.toUpperCase()}
                          sx={{
                            bgcolor: statusColors?.bg,
                            color: statusColors?.text,
                            fontWeight: 700,
                            border: `1px solid ${statusColors?.border}`,
                            fontSize: '0.75rem'
                          }}
                        />
                      );
                    }
                    return (
                      <Chip
                        label={deployment?.instance_status?.toUpperCase()}
                        sx={{
                          bgcolor: statusColors?.bg,
                          color: statusColors?.text,
                          fontWeight: 700,
                          border: `1px solid ${statusColors?.border}`,
                          fontSize: '0.75rem'
                        }}
                      />
                    );
                  })()}
                  <Typography variant="body2" color="text.secondary">
                    {deployment.model_name} • {deployment.modelVersion}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {deployment.instance_status === 'Stopped' ? (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => handleInstanceAction('start')}
                  sx={{
                    bgcolor: '#10b981',
                    color: 'white',
                    fontWeight: 700,
                    px: 3,
                    '&:hover': {
                      bgcolor: '#059669',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(16,185,129,0.3)'
                    },
                    transition: 'all 0.3s'
                  }}
                >
                  Start Instance
                </Button>
              ) : deployment.instance_status === 'Running' ? (
                <>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => handleInstanceAction('stop')}
                    startIcon={<StopIcon />}
                    sx={{
                      bgcolor: '#ef4444',
                      color: 'white',
                      fontWeight: 700,
                      px: 3,
                      '&:hover': {
                        bgcolor: '#dc2626',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(239,68,68,0.3)'
                      },
                      transition: 'all 0.3s'
                    }}
                  >
                    Stop Instance
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => handleInstanceAction('restart')}
                    startIcon={<StopIcon />}
                    sx={{
                      bgcolor: '#4455efff',
                      color: 'white',
                      fontWeight: 700,
                      px: 3,
                      '&:hover': {
                        bgcolor: '#4455efff',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(239,68,68,0.3)'
                      },
                      transition: 'all 0.3s'
                    }}
                  >
                    Restart Instance
                  </Button>
                </>
              ) : null}

              {/* <IconButton
                onClick={handleMenuOpen}
                sx={{
                  bgcolor: '#f8fafc',
                  color: '#64748b',
                  border: '1px solid #e2e8f0',
                  '&:hover': {
                    bgcolor: '#f1f5f9',
                    borderColor: '#cbd5e1'
                  }
                }}
              >
                <MoreVertIcon />
              </IconButton> */}

              {/* <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                  elevation: 3,
                  sx: { borderRadius: 2, minWidth: 180 }
                }}
              >
                <MenuItem onClick={handleMenuClose} sx={{ color: '#ef4444' }}>
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} />
                  </ListItemIcon>
                  <ListItemText>Delete</ListItemText>
                </MenuItem>
              </Menu> */}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mt: 4, position: 'relative', zIndex: 1 }}>
        {/* Tabs Section */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            mb: 4
          }}
        >
          <Box sx={{ borderBottom: '1px solid #e2e8f0', bgcolor: '#fafbfc' }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{
                px: 3,
                '& .MuiTab-root': {
                  color: '#64748b',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  minHeight: 64,
                  '&.Mui-selected': {
                    color: '#667eea',
                    fontWeight: 700
                  }
                },
                '& .MuiTabs-indicator': {
                  bgcolor: '#667eea',
                  height: 3,
                  borderRadius: '3px 3px 0 0'
                }
              }}
            >
              {/* <Tab icon={<InfoIcon />} iconPosition="start" label="Overview" /> disabled={!isRunning}*/}
              <Tab icon={<InfoIcon />} iconPosition="start" label="Overview" />
              <Tab icon={<ChatIcon />} iconPosition="start" label="Playground"  />
              <Tab icon={<CodeIcon />} iconPosition="start" label="API Integration"  disabled={!isRunning}/>

            </Tabs>
          </Box>

          {/* Playground Tab */}
          {activeTab === 0 && (
            <Box sx={{ p: 4 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                  
                  Deployment Configuration
                </Typography>
                    <Stack spacing={3}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontWeight: 700, display: 'block', mb: 1, letterSpacing: 1 }}>
                          Endpoint URL
                        </Typography>
                        <Paper 
                          elevation={0}
                          sx={{ 
                            p: 2, 
                            bgcolor: '#fafbfc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontFamily: 'monospace', 
                              color: '#0f172a',
                              fontSize: '13px',
                              wordBreak: 'break-all'
                            }}
                          >
                            {deployment.end_point}
                          </Typography>
                        </Paper>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid spacing={6}>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontWeight: 700, display: 'block', mb: 1, letterSpacing: 1 }}>
                              Replicas
                            </Typography>
                            <Box 
                              sx={{ 
                                p: 2,
                                bgcolor: '#fafbfc',
                                border: '1px solid #e2e8f0',
                                borderRadius: 2
                              }}
                            >
                              <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 600 }}>
                                {deployment.replicas} instances
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>

                        <Grid spacing={6}>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontWeight: 700, display: 'block', mb: 1, letterSpacing: 1 }}>
                              Auto Scaling
                            </Typography>
                            <Box 
                              sx={{ 
                                p: 2,
                                bgcolor: '#fafbfc',
                                border: '1px solid #e2e8f0',
                                borderRadius: 2
                              }}
                            >
                              <Chip
                                label={deployment.autoScaling ? 'Enabled' : 'Disabled'}
                                size="small"
                                sx={{
                                  bgcolor: deployment.autoScaling ? '#dcfce7' : '#f1f5f9',
                                  color: deployment.autoScaling ? '#166534' : '#64748b',
                                  fontWeight: 700,
                                  fontSize: '0.75rem'
                                }}
                              />
                            </Box>
                          </Box>
                        </Grid>

                        <Grid spacing={6}>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontWeight: 700, display: 'block', mb: 1, letterSpacing: 1 }}>
                              Guardrails
                            </Typography>
                            <Box 
                              sx={{ 
                                p: 2,
                                bgcolor: '#fafbfc',
                                border: '1px solid #e2e8f0',
                                borderRadius: 2
                              }}
                            >
                              <Chip
                                label={deployment?.guardrail_id ? 'Enabled' : 'Disabled'}
                                size="small"
                                sx={{
                                  bgcolor: deployment?.guardrail_id ? '#dcfce7' : '#f1f5f9',
                                  color: deployment?.guardrail_id ? '#166534' : '#64748b',
                                  fontWeight: 700,
                                  fontSize: '0.75rem'
                                }}
                              />
                            </Box>
                          </Box>
                        </Grid>

                        {/* <Grid spacing={6}>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontWeight: 700, display: 'block', mb: 1, letterSpacing: 1 }}>
                              Service name
                            </Typography>
                            <Box 
                              sx={{ 
                                p: 2,
                                bgcolor: '#fafbfc',
                                border: '1px solid #e2e8f0',
                                borderRadius: 2
                              }}
                            >
                              <Chip
                                label={deployment?.service_config?.service_name}
                                size="small"
                                sx={{
                                  bgcolor: deployment.autoScaling ? '#dcfce7' : '#f1f5f9',
                                  color: deployment.autoScaling ? '#166534' : '#64748b',
                                  fontWeight: 700,
                                  fontSize: '0.75rem'
                                }}
                              />
                            </Box>
                          </Box>
                        </Grid>
                        <Grid spacing={6}>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontWeight: 700, display: 'block', mb: 1, letterSpacing: 1 }}>
                              Service name
                            </Typography>
                            <Box 
                              sx={{ 
                                p: 2,
                                bgcolor: '#fafbfc',
                                border: '1px solid #e2e8f0',
                                borderRadius: 2
                              }}
                            >
                              <Chip
                                label={deployment?.service_config?.target_port}
                                size="small"
                                sx={{
                                  bgcolor: deployment.autoScaling ? '#dcfce7' : '#f1f5f9',
                                  color: deployment.autoScaling ? '#166534' : '#64748b',
                                  fontWeight: 700,
                                  fontSize: '0.75rem'
                                }}
                              />
                            </Box>
                          </Box>
                        </Grid> */}


                      </Grid>

                    </Stack>
                  </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <PlayGround deployment={deployment} />
          )}

          {/* API Tab */}
          {activeTab === 2 && (
            <APIIntegration deployment={deployment} />
          )}

          {/* Routing Tab */}
          {activeTab === 3 && (
            <Box sx={{ p: 4 }}>
              {!isRunning && (
                <Alert 
                  severity="warning" 
                  sx={{ 
                    mb: 3,
                    borderRadius: 2,
                    '& .MuiAlert-icon': {
                      color: '#f59e0b'
                    }
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Deployment Not Running
                  </Typography>
                  <Typography variant="body2">
                    {/* This deployment is currently {deployment?.status}. Start the deployment to configure routing. */}
                  </Typography>
                </Alert>
              )}

            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
